import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { canonicalCategory } from "../categories";
import ConvertInlineBar from "../components/ConvertInlineBar";
import SeriesEpisodesModal from "../components/SeriesEpisodesModal";
import Shell from "../components/Shell";
import UsersPanel from "../components/UsersPanel";
import MaintenancePanel from "../components/MaintenancePanel";
import { sagaInsights, previewRuleMatches, franchiseBase, suggestPatternsFromTitles } from "../sagas";

const emptyForm = {
  title: "",
  description: "",
  category: "",
  content_type: "movie",
  tmdb_id: "",
  tmdb_type: "",
  poster_url: "",
  admin_rating: "",
};

const CONTENT_TYPES = [
  { value: "movie", label: "Película" },
  { value: "series", label: "Serie" },
  { value: "anime_series", label: "Anime (serie)" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SERIES_TYPES = new Set(["series", "anime_series"]);
const SERIES_ONLY_TYPES = [
  { value: "series", label: "Serie" },
  { value: "anime_series", label: "Anime (serie)" },
];

function isSeriesEpisode(v) {
  if (SERIES_TYPES.has(v.content_type || "")) return true;
  if (v.series_title && (v.season_number != null || v.episode_number != null)) return true;
  return false;
}

function isAnimeMovie(v) {
  if (!v || isSeriesEpisode(v)) return false;
  if ((v.content_type || "") === "anime") return true;
  const cat = (v.category || "").toLowerCase();
  return (v.content_type || "movie") === "movie" && cat.includes("anime");
}

function isAnimeSeriesGroup(group) {
  if (!group || group.kind !== "series") return false;
  if (group.content_type === "anime_series") return true;
  const cat = (group.episodes?.[0]?.category || "").toLowerCase();
  return cat.includes("anime");
}

function seriesGroupKey(v) {
  if (v.tmdb_id) return `tmdb:${v.tmdb_id}`;
  const name = (v.series_title || "").trim().toLowerCase();
  if (name) return `title:${name}`;
  return `solo:${v.id}`;
}

function seriesAggregateStatus(episodes) {
  const statuses = episodes.map((e) => e.status);
  if (statuses.some((s) => s === "processing")) return "processing";
  if (statuses.some((s) => s === "queued")) return "queued";
  if (statuses.some((s) => s === "pending")) return "pending";
  if (statuses.some((s) => s === "failed") && !statuses.every((s) => s === "failed")) {
    return "processing";
  }
  if (statuses.every((s) => s === "ready")) return "ready";
  if (statuses.every((s) => s === "failed")) return "failed";
  if (statuses.some((s) => s === "failed")) return "failed";
  return statuses[0] || "pending";
}

function buildCatalogRows(list) {
  const rows = [];
  const seriesMap = new Map();

  for (const v of list) {
    if (!isSeriesEpisode(v)) {
      rows.push({ kind: "movie", video: v, sortAt: v.id });
      continue;
    }
    const key = seriesGroupKey(v);
    let group = seriesMap.get(key);
    if (!group) {
      group = {
        kind: "series",
        key,
        episodes: [],
        sortAt: v.id,
      };
      seriesMap.set(key, group);
      rows.push(group);
    }
    group.episodes.push(v);
    group.sortAt = Math.min(group.sortAt, v.id);
  }

  for (const group of seriesMap.values()) {
    group.episodes.sort((a, b) => {
      const sa = a.season_number ?? 0;
      const sb = b.season_number ?? 0;
      if (sa !== sb) return sa - sb;
      const ea = a.episode_number ?? 0;
      const eb = b.episode_number ?? 0;
      if (ea !== eb) return ea - eb;
      return a.id - b.id;
    });
    const first = group.episodes[0];
    group.title =
      first.series_title ||
      first.title?.split(" · ")[0] ||
      `Serie #${first.id}`;
    group.cover_url =
      group.episodes.find((e) => e.series_poster_url)?.series_poster_url ||
      group.episodes.find((e) => e.cover_url)?.cover_url ||
      null;
    group.content_type = first.content_type || "series";
    group.status = seriesAggregateStatus(group.episodes);
    group.readyCount = group.episodes.filter((e) => e.status === "ready").length;
    group.failedCount = group.episodes.filter((e) => e.status === "failed").length;
    group.active =
      group.episodes.find((e) => e.status === "processing") ||
      group.episodes.find((e) => e.status === "queued") ||
      null;
    const rated = group.episodes.find((e) => e.rating_avg != null);
    group.rating_avg = rated?.rating_avg ?? null;
    group.admin_rating = rated?.admin_rating ?? null;
    group.rating_count = rated?.rating_count ?? 0;
    group.error_message =
      group.episodes.find((e) => e.error_message)?.error_message || null;
  }

  rows.sort((a, b) => b.sortAt - a.sortAt);
  return rows;
}

export default function AdminPage() {
  const { token } = useAuth();
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  /** null | { key, episodeIds, title } — edición de ficha de serie/anime */
  const [editSeries, setEditSeries] = useState(null);
  const [tmdbQuery, setTmdbQuery] = useState("");
  const [tmdbResults, setTmdbResults] = useState([]);
  const [tmdbBusy, setTmdbBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [adminTab, setAdminTab] = useState("movies");
  const [catalogQuery, setCatalogQuery] = useState("");
  const [ratingsQuery, setRatingsQuery] = useState("");
  const [seriesModal, setSeriesModal] = useState(null);
  const [tmdbPicked, setTmdbPicked] = useState(false);
  const [sagaRules, setSagaRules] = useState([]);
  const [sagaForm, setSagaForm] = useState({
    id: null,
    title: "",
    patternsText: "",
    videoIdsText: "",
  });
  const [sagaBusy, setSagaBusy] = useState(false);
  const [storage, setStorage] = useState(null);

  function load() {
    return api("/api/videos?role_view=admin&status=all", { token }).then(setVideos);
  }

  function formatBytes(n) {
    const v = Number(n) || 0;
    if (v < 1024) return `${v} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let x = v;
    let i = -1;
    do {
      x /= 1024;
      i += 1;
    } while (x >= 1024 && i < units.length - 1);
    return `${x.toFixed(x >= 10 ? 0 : 1)} ${units[i]}`;
  }

  function loadSagaRules() {
    return api("/api/sagas/rules?all=1", { token }).then((data) => {
      setSagaRules(Array.isArray(data) ? data : []);
    });
  }

  useEffect(() => {
    let cancelled = false;
    function refresh() {
      return api("/api/videos?role_view=admin&status=all", { token })
        .then((all) => {
          if (!cancelled) setVideos(all);
        })
        .catch((err) => {
          if (!cancelled) setError(err.message);
        });
    }
    refresh();
    loadSagaRules().catch(() => {});
    function loadStorage() {
      return api("/api/admin/storage", { token })
        .then((data) => {
          if (!cancelled) setStorage(data);
        })
        .catch(() => {
          if (!cancelled) setStorage(null);
        });
    }
    loadStorage();
    const timer = setInterval(() => refresh().catch(() => {}), 5000);
    const storageTimer = setInterval(() => loadStorage().catch(() => {}), 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      clearInterval(storageTimer);
    };
  }, [token]);

  async function searchTmdb(e) {
    e?.preventDefault?.();
    const q = tmdbQuery.trim();
    if (!q) return;
    setTmdbBusy(true);
    setError("");
    try {
      const data = await api(`/api/tmdb/search?q=${encodeURIComponent(q)}&type=multi`, {
        token,
      });
      setTmdbResults(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setTmdbBusy(false);
    }
  }

  function pickTmdb(item) {
    setTmdbPicked(true);
    setForm((prev) => {
      let contentType =
        item.content_type === "anime"
          ? "movie"
          : item.content_type || prev.content_type || "movie";
      if (editSeries) {
        contentType =
          item.content_type === "anime_series" ||
          (item.category || "").toLowerCase().includes("anime")
            ? "anime_series"
            : "series";
      }
      return {
        ...prev,
        title: item.title || prev.title,
        description: item.overview || "",
        category: item.category || prev.category,
        content_type: contentType,
        tmdb_id: String(item.tmdb_id),
        tmdb_type: item.media_type,
        poster_url: item.poster_url || "",
      };
    });
  }

  function startEdit(video) {
    setEditSeries(null);
    setEditId(video.id);
    setTmdbPicked(false);
    setForm({
      title: video.title || "",
      description: video.description || "",
      category: video.category || "",
      content_type:
        video.content_type === "anime" ? "movie" : video.content_type || "movie",
      tmdb_id: video.tmdb_id ? String(video.tmdb_id) : "",
      tmdb_type: video.tmdb_media_type || "",
      poster_url: video.cover_url || "",
      admin_rating:
        video.admin_rating != null
          ? String(video.admin_rating)
          : video.rating_avg != null
            ? String(video.rating_avg)
            : "",
    });
    setTmdbQuery(video.title || "");
    setTmdbResults([]);
    setMessage(`Editando #${video.id}`);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startEditSeries(group) {
    if (!group?.episodes?.length) return;
    const first = group.episodes[0];
    const contentType = isAnimeSeriesGroup(group)
      ? "anime_series"
      : group.content_type === "anime_series"
        ? "anime_series"
        : "series";
    setEditId(null);
    setEditSeries({
      key: group.key,
      episodeIds: group.episodes.map((e) => e.id),
      title: group.title,
    });
    setTmdbPicked(false);
    setForm({
      title: group.title || first.series_title || "",
      description: first.description || "",
      category: first.category || "",
      content_type: contentType,
      tmdb_id: first.tmdb_id ? String(first.tmdb_id) : "",
      tmdb_type: first.tmdb_media_type || "tv",
      poster_url: group.cover_url || first.cover_url || "",
      admin_rating:
        group.admin_rating != null
          ? String(group.admin_rating)
          : first.admin_rating != null
            ? String(first.admin_rating)
            : "",
    });
    setTmdbQuery(group.title || first.series_title || "");
    setTmdbResults([]);
    setMessage(`Editando ficha · ${group.title} (${group.episodes.length} caps.)`);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelEdit() {
    setEditId(null);
    setEditSeries(null);
    setTmdbPicked(false);
    setForm(emptyForm);
    setTmdbResults([]);
    setTmdbQuery("");
    setMessage("");
  }

  async function saveMetadata(e) {
    e.preventDefault();
    if (!editId && !editSeries) return;
    setBusy(true);
    setError("");
    try {
      if (editSeries) {
        const body = {
          episode_ids: editSeries.episodeIds,
          series_title: form.title,
          description: form.description,
          category: canonicalCategory(form.category),
          content_type:
            form.content_type === "anime_series" ? "anime_series" : "series",
          admin_rating: form.admin_rating === "" ? null : Number(form.admin_rating),
        };
        if (tmdbPicked && form.tmdb_id) {
          body.tmdb_id = Number(form.tmdb_id);
          body.tmdb_type = form.tmdb_type || "tv";
          body.apply_tmdb = true;
          body.enrich_episodes = true;
        }
        const res = await api("/api/videos/series-meta", {
          method: "PUT",
          token,
          body,
        });
        const n = res?.updated ?? editSeries.episodeIds.length;
        const errN = Array.isArray(res?.errors) ? res.errors.length : 0;
        if (!n) {
          throw new Error(res?.error || "No se pudo guardar la ficha de la serie.");
        }
        setMessage(
          errN
            ? `Ficha guardada en ${n} capítulo(s) (${errN} con aviso TMDB).`
            : `Ficha de serie guardada en ${n} capítulo(s).`
        );
        cancelEdit();
        await load();
        return;
      }

      const body = {
        title: form.title,
        description: form.description,
        category: canonicalCategory(form.category),
        content_type: form.content_type || "movie",
        admin_rating: form.admin_rating === "" ? null : Number(form.admin_rating),
      };
      // Solo reaplicar TMDB si el admin eligió un resultado nuevo.
      if (tmdbPicked && form.tmdb_id) {
        body.tmdb_id = Number(form.tmdb_id);
        body.tmdb_type = form.tmdb_type;
        body.apply_tmdb = true;
        body.replace_cover = true;
      }
      await api(`/api/videos/${editId}`, { method: "PUT", token, body });
      setMessage("Cambios guardados.");
      cancelEdit();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveAdminRating(videoId, value) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api(`/api/videos/${videoId}`, {
        method: "PUT",
        token,
        body: { admin_rating: value },
      });
      setMessage(
        value == null
          ? `Nota admin quitada del vídeo #${videoId}.`
          : `Nota ${Number(value).toFixed(1)} guardada en #${videoId}.`
      );
      await load();
    } catch (err) {
      setError(err.message || "No se pudo guardar la puntuación.");
    } finally {
      setBusy(false);
    }
  }

  async function reprocess(id) {
    try {
      await api(`/api/videos/${id}/reprocess`, { method: "POST", token });
      setMessage(`Vídeo #${id} encolado para reprocesar (multi-audio).`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function reprocessAll() {
    if (
      !window.confirm(
        "¿Reprocesar TODOS los vídeos? Generará HLS con idiomas separados. Puede tardar mucho."
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const data = await api(`/api/videos/reprocess-all`, { method: "POST", token });
      setMessage(
        `Encolados ${data.queued || 0} vídeos para multi-audio. Deja el worker RQ en marcha.`
      );
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function reprocessSeries(episodes) {
    if (!episodes?.length) return;
    if (
      !window.confirm(
        `¿Reprocesar ${episodes.length} capítulo(s) de esta serie?`
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      let ok = 0;
      for (const ep of episodes) {
        try {
          await api(`/api/videos/${ep.id}/reprocess`, { method: "POST", token });
          ok += 1;
        } catch (_) {
          /* continue */
        }
      }
      setMessage(`Encolados ${ok}/${episodes.length} capítulos.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeVideo(id) {
    if (!window.confirm("¿Borrar este vídeo?")) return;
    try {
      await api(`/api/videos/${id}`, { method: "DELETE", token });
      if (editId === id) cancelEdit();
      if (editSeries?.episodeIds?.includes(id)) cancelEdit();
      setSeriesModal((prev) => {
        if (!prev) return prev;
        const nextEps = prev.episodes.filter((e) => e.id !== id);
        if (!nextEps.length) return null;
        return { ...prev, episodes: nextEps };
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function removeSeries(episodes) {
    if (!episodes?.length) return;
    if (
      !window.confirm(
        `¿Borrar los ${episodes.length} capítulos de esta serie? Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      for (const ep of episodes) {
        try {
          await api(`/api/videos/${ep.id}`, { method: "DELETE", token });
        } catch (_) {
          /* continue */
        }
      }
      setSeriesModal(null);
      if (episodes.some((e) => e.id === editId)) cancelEdit();
      if (
        editSeries &&
        episodes.some((e) => editSeries.episodeIds.includes(e.id))
      ) {
        cancelEdit();
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const filteredVideos = useMemo(() => {
    const term = catalogQuery.trim().toLowerCase();
    if (!term) return videos;
    return videos.filter((v) => {
      const hay = [
        v.title,
        v.category,
        v.content_type,
        v.status,
        v.error_message,
        String(v.id),
        v.series_title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [videos, catalogQuery]);

  const filteredRatings = useMemo(() => {
    const term = ratingsQuery.trim().toLowerCase();
    if (!term) return videos;
    return videos.filter((v) => {
      const hay = [v.title, v.category, String(v.id)].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [videos, ratingsQuery]);

  const catalogRows = useMemo(() => buildCatalogRows(filteredVideos), [filteredVideos]);
  const allSeriesRows = useMemo(
    () => catalogRows.filter((r) => r.kind === "series"),
    [catalogRows]
  );
  const movieRows = useMemo(
    () =>
      catalogRows.filter((r) => r.kind === "movie" && !isAnimeMovie(r.video)),
    [catalogRows]
  );
  const seriesRows = useMemo(
    () => allSeriesRows.filter((r) => !isAnimeSeriesGroup(r)),
    [allSeriesRows]
  );
  const animeRows = useMemo(() => {
    const seriesAnime = allSeriesRows.filter((r) => isAnimeSeriesGroup(r));
    const movieAnime = catalogRows.filter(
      (r) => r.kind === "movie" && isAnimeMovie(r.video)
    );
    return [...seriesAnime, ...movieAnime].sort((a, b) => b.sortAt - a.sortAt);
  }, [allSeriesRows, catalogRows]);
  const activeCatalogRows =
    adminTab === "series"
      ? seriesRows
      : adminTab === "anime"
        ? animeRows
        : movieRows;
  const isCatalogTab =
    adminTab === "movies" || adminTab === "series" || adminTab === "anime";
  const catalogKindLabel =
    adminTab === "series" ? "serie" : adminTab === "anime" ? "anime" : "película";
  const catalogKindLabelPlural =
    adminTab === "series"
      ? "series"
      : adminTab === "anime"
        ? "títulos anime"
        : "películas";

  const totalPages = Math.max(1, Math.ceil(activeCatalogRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return activeCatalogRows.slice(start, start + pageSize);
  }, [activeCatalogRows, safePage, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [catalogQuery, pageSize, adminTab]);

  // Mantener el modal de serie sincronizado con el catálogo en vivo.
  useEffect(() => {
    setSeriesModal((prev) => {
      if (!prev) return prev;
      const fresh = allSeriesRows.find(
        (r) => r.kind === "series" && r.key === prev.key
      );
      return fresh || null;
    });
  }, [allSeriesRows]);

  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    const buttons = [];
    for (let i = start; i <= end; i += 1) buttons.push(i);
    return buttons;
  }, [safePage, totalPages]);

  const rangeFrom = activeCatalogRows.length ? (safePage - 1) * pageSize + 1 : 0;
  const rangeTo = Math.min(safePage * pageSize, activeCatalogRows.length);

  const sagasInfo = useMemo(() => sagaInsights(videos, sagaRules), [videos, sagaRules]);

  const sagaPreview = useMemo(() => {
    const patterns = sagaForm.patternsText
      .split(/[\n|]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    const video_ids = sagaForm.videoIdsText
      .split(/[\s,;]+/)
      .map((p) => Number(p))
      .filter((n) => Number.isFinite(n) && n > 0);
    return previewRuleMatches(videos, { patterns, video_ids });
  }, [videos, sagaForm.patternsText, sagaForm.videoIdsText]);

  function resetSagaForm() {
    setSagaForm({ id: null, title: "", patternsText: "", videoIdsText: "" });
  }

  function editSagaRule(rule) {
    setSagaForm({
      id: rule.id,
      title: rule.title || "",
      patternsText: (rule.patterns || []).join("\n"),
      videoIdsText: (rule.video_ids || []).join(", "),
    });
    setAdminTab("sagas");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function promoteDiscoveredSaga(saga) {
    const patterns = suggestPatternsFromTitles(saga.items.map((v) => v.title));
    setSagaForm({
      id: null,
      title: saga.title || "",
      patternsText: patterns.join("\n"),
      videoIdsText: saga.items.map((v) => v.id).join(", "),
    });
    setMessage(`Regla preparada para «${saga.title}». Revisa y guarda.`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function saveSagaRule(e) {
    e?.preventDefault?.();
    const title = sagaForm.title.trim();
    const patterns = sagaForm.patternsText
      .split(/[\n|]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    const video_ids = sagaForm.videoIdsText
      .split(/[\s,;]+/)
      .map((p) => Number(p))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!title) {
      setError("Pon un nombre a la saga.");
      return;
    }
    if (!patterns.length && !video_ids.length) {
      setError("Añade palabras clave o IDs de películas.");
      return;
    }
    setSagaBusy(true);
    setError("");
    try {
      const body = { title, patterns, video_ids, enabled: true };
      if (sagaForm.id) {
        await api(`/api/sagas/rules/${sagaForm.id}`, { method: "PUT", token, body });
        setMessage(`Saga «${title}» actualizada.`);
      } else {
        await api("/api/sagas/rules", { method: "POST", token, body });
        setMessage(`Saga «${title}» creada.`);
      }
      resetSagaForm();
      await loadSagaRules();
    } catch (err) {
      setError(err.message || "No se pudo guardar la saga.");
    } finally {
      setSagaBusy(false);
    }
  }

  async function deleteSagaRule(rule) {
    if (!window.confirm(`¿Borrar la regla «${rule.title}»?`)) return;
    setSagaBusy(true);
    setError("");
    try {
      await api(`/api/sagas/rules/${rule.id}`, { method: "DELETE", token });
      setMessage(`Regla «${rule.title}» borrada.`);
      if (sagaForm.id === rule.id) resetSagaForm();
      await loadSagaRules();
    } catch (err) {
      setError(err.message || "No se pudo borrar.");
    } finally {
      setSagaBusy(false);
    }
  }

  return (
    <Shell wide>
      <div className="page admin-page admin-page-wide">
      {storage?.disks?.length ? (
        <section className="admin-storage-panel admin-fade-in" aria-label="Estado del disco">
          {storage.disks.map((disk) => (
            <div className="admin-storage-card" key={`${disk.kind}-${disk.path}`}>
              <h3>{disk.label}</h3>
              <p className="admin-storage-path">{disk.path || "—"}</p>
              {disk.mounted && disk.total_bytes > 0 ? (
                <>
                  <div
                    className={`admin-storage-bar ${disk.percent_used >= 85 ? "warn" : ""}`}
                    title={`${disk.percent_used}% usado`}
                  >
                    <i style={{ width: `${Math.min(100, disk.percent_used)}%` }} />
                  </div>
                  <p className="admin-storage-meta">
                    {formatBytes(disk.used_bytes)} usados · {formatBytes(disk.free_bytes)} libres ·{" "}
                    {formatBytes(disk.total_bytes)} total ({disk.percent_used}%)
                  </p>
                </>
              ) : (
                <p className="admin-storage-offline">
                  {disk.hint || "No montado o sin acceso"}
                </p>
              )}
            </div>
          ))}
        </section>
      ) : null}
      <section className="admin-header admin-fade-in">
        <div className="admin-header-row">
          <div className="admin-tabs-scroller">
          <div className="admin-tabs" role="tablist" aria-label="Secciones admin">
            <button
              type="button"
              role="tab"
              aria-selected={adminTab === "movies"}
              className={`admin-tab ${adminTab === "movies" ? "active" : ""}`}
              onClick={() => setAdminTab("movies")}
            >
              <span className="admin-tab-full">Películas</span>
              <span className="admin-tab-short">Cine</span>
              <span className="admin-tab-badge" title="Películas en catálogo">
                {movieRows.length}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={adminTab === "series"}
              className={`admin-tab ${adminTab === "series" ? "active" : ""}`}
              onClick={() => setAdminTab("series")}
            >
              <span className="admin-tab-full">Series</span>
              <span className="admin-tab-short">Series</span>
              <span className="admin-tab-badge" title="Series (no anime)">
                {seriesRows.length}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={adminTab === "anime"}
              className={`admin-tab ${adminTab === "anime" ? "active" : ""}`}
              onClick={() => setAdminTab("anime")}
            >
              <span className="admin-tab-full">Anime</span>
              <span className="admin-tab-short">Anime</span>
              <span className="admin-tab-badge" title="Series anime y películas anime">
                {animeRows.length}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={adminTab === "ratings"}
              className={`admin-tab ${adminTab === "ratings" ? "active" : ""}`}
              onClick={() => setAdminTab("ratings")}
            >
              <span className="admin-tab-full">Votación</span>
              <span className="admin-tab-short">Notas</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={adminTab === "sagas"}
              className={`admin-tab ${adminTab === "sagas" ? "active" : ""}`}
              onClick={() => setAdminTab("sagas")}
            >
              <span className="admin-tab-full">Sagas</span>
              <span className="admin-tab-short">Sagas</span>
              {sagasInfo.newCount > 0 ? (
                <span className="admin-tab-badge" title="Sagas nuevas auto-detectadas">
                  {sagasInfo.newCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={adminTab === "users"}
              className={`admin-tab ${adminTab === "users" ? "active" : ""}`}
              onClick={() => setAdminTab("users")}
            >
              <span className="admin-tab-full">Usuarios</span>
              <span className="admin-tab-short">Cuentas</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={adminTab === "maintenance"}
              className={`admin-tab ${adminTab === "maintenance" ? "active" : ""}`}
              onClick={() => setAdminTab("maintenance")}
            >
              <span className="admin-tab-full">Mantenimiento</span>
              <span className="admin-tab-short">Mant.</span>
            </button>
          </div>
          </div>
          {isCatalogTab ? (
            <div className="admin-header-actions">
              <button type="button" disabled={busy} onClick={reprocessAll}>
                <span className="admin-tab-full">Reprocesar todo (multi-audio)</span>
                <span className="admin-tab-short">Reprocesar</span>
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {adminTab === "maintenance" ? (
        <div className="admin-tab-panel admin-fade-in" key="maintenance">
          {message ? <p className="ok">{message}</p> : null}
          {error ? <p className="error">{error}</p> : null}
          <MaintenancePanel
            onMessage={(msg) => {
              setMessage(msg);
              setError("");
            }}
            onError={(msg) => {
              setError(msg);
              setMessage("");
            }}
          />
        </div>
      ) : adminTab === "users" ? (
        <div className="admin-tab-panel admin-fade-in" key="users">
          <UsersPanel />
        </div>
      ) : adminTab === "sagas" ? (
        <div className="admin-tab-panel admin-fade-in" key="sagas">
          <div className="hint-panel">
            <p>
              Crea sagas con un <strong>nombre</strong> y <strong>palabras clave</strong>{" "}
              (una por línea). Si el título de la película contiene alguna, entra en la saga.
              También puedes fijar IDs concretos. Hace falta ≥2 películas listas.
            </p>
            <p className="muted" style={{ marginBottom: 0 }}>
              {sagasInfo.customCount} reglas tuyas · {sagasInfo.activeCount} visibles en
              catálogo · {sagasInfo.newCount} auto-detectadas
            </p>
          </div>

          {message ? <p className="ok">{message}</p> : null}
          {error ? <p className="error">{error}</p> : null}

          <form className="edit-form admin-saga-form" onSubmit={saveSagaRule}>
            <h2 className="full">
              {sagaForm.id ? `Editando regla #${sagaForm.id}` : "Nueva saga / regla"}
            </h2>
            <label>
              Nombre de la saga
              <input
                required
                value={sagaForm.title}
                onChange={(e) => setSagaForm({ ...sagaForm, title: e.target.value })}
                placeholder="Ej. Rápidos y furiosos"
              />
            </label>
            <label className="full">
              Palabras clave (una por línea)
              <textarea
                rows={4}
                value={sagaForm.patternsText}
                onChange={(e) =>
                  setSagaForm({ ...sagaForm, patternsText: e.target.value })
                }
                placeholder={"rapidos y furiosos\nfast and furious\nfast & furious"}
              />
            </label>
            <label className="full">
              IDs de películas (opcional, separados por coma)
              <input
                value={sagaForm.videoIdsText}
                onChange={(e) =>
                  setSagaForm({ ...sagaForm, videoIdsText: e.target.value })
                }
                placeholder="12, 45, 90"
              />
            </label>
            <div className="full admin-saga-preview">
              <p className="muted">
                Vista previa: <strong>{sagaPreview.length}</strong> coincidencia
                {sagaPreview.length === 1 ? "" : "s"}
                {sagaPreview.length >= 2 ? " · lista para mostrarse" : ""}
              </p>
              {sagaPreview.length ? (
                <p className="admin-saga-titles">
                  {sagaPreview.map((v) => `#${v.id} ${v.title}`).join(" · ")}
                </p>
              ) : (
                <p className="muted">Ninguna película coincide todavía.</p>
              )}
            </div>
            <div className="full actions">
              <button type="submit" disabled={sagaBusy}>
                {sagaBusy ? "Guardando…" : sagaForm.id ? "Guardar cambios" : "Crear saga"}
              </button>
              {sagaForm.id || sagaForm.title ? (
                <button type="button" className="ghost" onClick={resetSagaForm}>
                  Limpiar
                </button>
              ) : null}
            </div>
          </form>

          {sagaRules.length ? (
            <section className="admin-saga-block">
              <h2>Tus reglas ({sagaRules.length})</h2>
              <ul className="admin-saga-list">
                {sagaRules.map((rule) => {
                  const matches = previewRuleMatches(videos, rule);
                  return (
                    <li key={rule.id} className="admin-saga-card">
                      <div style={{ flex: 1 }}>
                        <strong>{rule.title}</strong>
                        <p className="muted">
                          /sagas/{rule.slug} · {matches.length} película
                          {matches.length === 1 ? "" : "s"}
                          {!rule.enabled ? " · desactivada" : ""}
                        </p>
                        <p className="admin-saga-titles">
                          Claves: {(rule.patterns || []).join(" · ") || "—"}
                          {(rule.video_ids || []).length
                            ? ` · IDs: ${rule.video_ids.join(", ")}`
                            : ""}
                        </p>
                        {matches.length ? (
                          <p className="admin-saga-titles">
                            {matches.map((v) => v.title).join(" · ")}
                          </p>
                        ) : null}
                      </div>
                      <div className="actions" style={{ flexDirection: "column" }}>
                        <button type="button" onClick={() => editSagaRule(rule)}>
                          Editar
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          disabled={sagaBusy}
                          onClick={() => deleteSagaRule(rule)}
                        >
                          Borrar
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {sagasInfo.discovered.length ? (
            <section className="admin-saga-block">
              <h2>
                Detectadas automáticamente
                <span className="admin-tab-badge">{sagasInfo.discovered.length}</span>
              </h2>
              <p className="muted">
                Ya se ven en /sagas. Pulsa «Hacer regla» para fijarlas y poder editarlas.
              </p>
              <ul className="admin-saga-list">
                {sagasInfo.discovered.map((s) => (
                  <li key={s.slug} className="admin-saga-card is-new">
                    {s.cover_url ? (
                      <img className="admin-thumb" src={s.cover_url} alt="" />
                    ) : (
                      <div className="admin-thumb fallback">—</div>
                    )}
                    <div style={{ flex: 1 }}>
                      <strong>{s.title}</strong>
                      <p className="muted">
                        {s.count} películas · base «{franchiseBase(s.items[0]?.title)}»
                      </p>
                      <p className="admin-saga-titles">
                        {s.items.map((v) => v.title).join(" · ")}
                      </p>
                    </div>
                    <button type="button" onClick={() => promoteDiscoveredSaga(s)}>
                      Hacer regla
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {sagasInfo.incomplete.length ? (
            <section className="admin-saga-block">
              <h2>Casi listas (1 película)</h2>
              <ul className="admin-saga-list">
                {sagasInfo.incomplete.map((s) => (
                  <li key={s.slug} className="admin-saga-card">
                    <div>
                      <strong>{s.title}</strong>
                      <p className="muted">{s.hint}</p>
                      <p className="admin-saga-titles">
                        {s.items.map((v) => v.title).join(" · ")}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="admin-saga-block">
            <h2>Visibles en catálogo ({sagasInfo.activeCount})</h2>
            <ul className="admin-saga-list">
              {sagasInfo.active.map((s) => (
                <li key={s.slug} className="admin-saga-card">
                  {s.cover_url ? (
                    <img className="admin-thumb" src={s.cover_url} alt="" />
                  ) : (
                    <div className="admin-thumb fallback">—</div>
                  )}
                  <div>
                    <strong>{s.title}</strong>
                    <p className="muted">
                      {s.count} películas · {s.source === "custom" ? "regla admin" : "fija"} ·
                      /sagas/{s.slug}
                    </p>
                    <p className="admin-saga-titles">
                      {s.items.map((v) => v.title).join(" · ")}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      ) : adminTab === "ratings" ? (
        <div className="admin-tab-panel admin-fade-in" key="ratings">
          <div className="admin-toolbar">
            <label className="admin-search">
              <span className="sr-only">Buscar en votación</span>
              <input
                value={ratingsQuery}
                onChange={(e) => setRatingsQuery(e.target.value)}
                placeholder="Buscar título, categoría o ID…"
              />
            </label>
            <p className="muted admin-toolbar-meta">
              {filteredRatings.length} de {videos.length} vídeos
            </p>
          </div>
          {message ? <p className="ok">{message}</p> : null}
          {error ? <p className="error">{error}</p> : null}
          <div className="admin-ratings-list">
            {filteredRatings.length ? (
              filteredRatings.map((v, index) => {
                const current =
                  v.admin_rating != null
                    ? Number(v.admin_rating)
                    : v.rating_avg != null
                      ? Number(v.rating_avg)
                      : 0;
                return (
                  <article
                    key={v.id}
                    className="admin-rating-card admin-stagger"
                    style={{ "--i": index }}
                  >
                    {v.cover_url ? (
                      <img className="admin-thumb" src={v.cover_url} alt="" />
                    ) : (
                      <div className="admin-thumb fallback">—</div>
                    )}
                    <div className="admin-rating-card-body">
                      <div className="admin-rating-card-head">
                        <strong>{v.title}</strong>
                        <span className="muted">#{v.id}</span>
                      </div>
                      <p className="muted admin-rating-meta">
                        {v.admin_rating != null
                          ? `Nota admin: ${Number(v.admin_rating).toFixed(1)}`
                          : "Sin nota admin"}
                        {v.rating_count
                          ? ` · Usuarios: ${
                              v.user_rating_avg != null
                                ? Number(v.user_rating_avg).toFixed(1)
                                : "—"
                            } (${v.rating_count})`
                          : " · Sin votos de usuarios"}
                      </p>
                      <div className="admin-rating-stars" role="group" aria-label={`Nota de ${v.title}`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            className={`admin-star ${current >= n ? "on" : ""}`}
                            disabled={busy}
                            aria-label={`${n} estrellas`}
                            onClick={() => saveAdminRating(v.id, n)}
                          >
                            ★
                          </button>
                        ))}
                        <button
                          type="button"
                          className="ghost admin-rating-clear"
                          disabled={busy || v.admin_rating == null}
                          onClick={() => saveAdminRating(v.id, null)}
                        >
                          Quitar nota admin
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <p className="muted">No hay vídeos que coincidan.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="admin-tab-panel admin-fade-in" key={adminTab}>
      {editId || editSeries ? (
        <>
          <div className="tmdb-box">
            <form className="tmdb-search" onSubmit={searchTmdb}>
              <label className="full">
                Buscar en TMDB (opcional, para corregir ficha)
                <input
                  value={tmdbQuery}
                  onChange={(e) => setTmdbQuery(e.target.value)}
                  placeholder={
                    editSeries
                      ? "Nombre de la serie o anime"
                      : "Nombre de la película o serie"
                  }
                />
              </label>
              <button type="submit" disabled={tmdbBusy}>
                {tmdbBusy ? "Buscando…" : "Buscar"}
              </button>
            </form>
            {tmdbResults.length ? (
              <div className="tmdb-results">
                {tmdbResults.map((item) => (
                  <button
                    type="button"
                    key={`${item.media_type}-${item.tmdb_id}`}
                    className={`tmdb-card ${
                      String(form.tmdb_id) === String(item.tmdb_id) ? "selected" : ""
                    }`}
                    onClick={() => pickTmdb(item)}
                  >
                    {item.poster_url ? (
                      <img src={item.poster_url} alt={item.title} />
                    ) : (
                      <div className="tmdb-fallback">?</div>
                    )}
                    <span>
                      <strong>{item.title}</strong>
                      <small>
                        {item.content_type === "anime_series"
                          ? "Anime (serie)"
                          : item.media_type === "movie"
                            ? item.category === "Anime"
                              ? "Película · Anime"
                              : "Película"
                            : "Serie"}
                        {item.year ? ` · ${item.year}` : ""}
                      </small>
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            {form.poster_url ? (
              <div className="tmdb-selected">
                <img src={form.poster_url} alt={form.title} />
                <div>
                  <strong>{form.title}</strong>
                  <p className="muted">Portada / ficha seleccionada</p>
                </div>
              </div>
            ) : null}
          </div>

          <form className="edit-form" onSubmit={saveMetadata}>
            <h2 className="full">
              {editSeries
                ? `Ficha · ${editSeries.title} (${editSeries.episodeIds.length} caps.)`
                : `Editando #${editId}`}
            </h2>
            <label>
              {editSeries ? "Nombre de la serie" : "Título"}
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label>
              Tipo
              <select
                value={form.content_type}
                onChange={(e) => setForm({ ...form, content_type: e.target.value })}
              >
                {(editSeries ? SERIES_ONLY_TYPES : CONTENT_TYPES).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Categoría
              <input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                onBlur={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    category: canonicalCategory(e.target.value),
                  }))
                }
                placeholder="Ej. Acción, Anime, Comedia, Terror…"
                list="admin-genre-suggestions"
              />
              <datalist id="admin-genre-suggestions">
                {[
                  "Acción",
                  "Aventura",
                  "Animación",
                  "Anime",
                  "Comedia",
                  "Crimen",
                  "Ciencia ficción",
                  "Drama",
                  "Fantasía",
                  "Terror",
                  "Suspense",
                  "Misterio",
                  "Romance",
                  "Música",
                  "Familia",
                  "Superhéroes",
                ].map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </label>
            <p className="muted full" style={{ marginTop: "-0.5rem" }}>
              {editSeries ? (
                <>
                  Cambia el tipo a <strong>Serie</strong> o <strong>Anime (serie)</strong>{" "}
                  para moverla de pestaña. Al guardar se aplica a todos los capítulos.
                </>
              ) : (
                <>
                  Películas anime: tipo <strong>Película</strong> y categoría{" "}
                  <strong>Anime</strong> (aparecen en la pestaña Anime).
                </>
              )}
            </p>
            <label>
              Puntuación (1–5)
              <input
                type="number"
                min="1"
                max="5"
                step="0.1"
                value={form.admin_rating}
                placeholder="Ej. 4.5"
                onChange={(e) => setForm({ ...form, admin_rating: e.target.value })}
              />
            </label>
            <p className="muted full" style={{ margin: 0 }}>
              {editSeries
                ? "La nota se aplica a todos los capítulos de la serie."
                : "La nota del admin se guarda en la base de datos y se muestra en el catálogo. Déjala vacía y guarda para quitarla."}
            </p>
            <label className="full">
              Sinopsis
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <div className="form-actions full">
              <button type="submit" disabled={busy}>
                Guardar
              </button>
              <button type="button" className="ghost" onClick={cancelEdit}>
                Cancelar
              </button>
            </div>
          </form>
        </>
      ) : null}

      {message ? <p className="ok">{message}</p> : null}
      {error ? <p className="error">{error}</p> : null}

      <div className="admin-toolbar">
        <label className="admin-search">
          <span className="sr-only">Buscar {catalogKindLabelPlural}</span>
          <input
            value={catalogQuery}
            onChange={(e) => setCatalogQuery(e.target.value)}
            placeholder={
              adminTab === "series"
                ? "Buscar serie, temporada, estado o ID…"
                : adminTab === "anime"
                  ? "Buscar anime, temporada, estado o ID…"
                  : "Buscar película, categoría, estado o ID…"
            }
          />
        </label>
        <p className="muted admin-toolbar-meta">
          {activeCatalogRows.length} {catalogKindLabel}
          {activeCatalogRows.length === 1 ? "" : adminTab === "anime" ? "s" : "s"}
          {" · "}
          {adminTab === "series" || adminTab === "anime"
            ? `${activeCatalogRows
                .filter((r) => r.kind === "series")
                .reduce((n, r) => n + (r.episodes?.length || 0), 0)} capítulos`
            : `${movieRows.length} vídeos`}
        </p>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table admin-table-striped">
          <thead>
            <tr>
              <th>Portada</th>
              <th>ID</th>
              <th>Título</th>
              <th>Tipo</th>
              <th>Nota</th>
              <th>Estado</th>
              <th>Conversión</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((row, index) => {
              if (row.kind === "series") {
                const converting = Boolean(row.active);
                return (
                  <tr
                    key={row.key}
                    className="admin-row admin-stagger admin-row-series"
                    style={{ "--i": index }}
                    onClick={() => setSeriesModal(row)}
                  >
                    <td>
                      {row.cover_url ? (
                        <img className="admin-thumb" src={row.cover_url} alt={row.title} />
                      ) : (
                        <div className="admin-thumb fallback">—</div>
                      )}
                    </td>
                    <td data-label="ID">
                      <span className="muted">{row.episodes.length} eps</span>
                    </td>
                    <td data-label="Título">
                      <strong>{row.title}</strong>
                      <div className="muted admin-series-sub">
                        {row.readyCount}/{row.episodes.length} listos
                        {row.failedCount ? ` · ${row.failedCount} error` : ""}
                        {" · "}
                        clic para capítulos
                      </div>
                    </td>
                    <td data-label="Tipo">
                      <span className="type-pill">
                        {CONTENT_TYPES.find((t) => t.value === row.content_type)?.label ||
                          "Serie"}
                      </span>
                    </td>
                    <td data-label="Nota">
                      {row.rating_avg != null ? (
                        <span className="admin-rating-pill">
                          ★ {Number(row.rating_avg).toFixed(1)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td data-label="Estado">
                      <span className={`badge badge-${row.status}`}>{row.status}</span>
                    </td>
                    <td
                      data-label="Conversión"
                      className="admin-convert-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {converting ? (
                        <ConvertInlineBar
                          key={`convert-${row.active.id}`}
                          token={token}
                          videoId={row.active.id}
                          enabled
                          label={
                            row.active.season_number != null &&
                            row.active.episode_number != null
                              ? `T${String(row.active.season_number).padStart(2, "0")}E${String(
                                  row.active.episode_number
                                ).padStart(2, "0")}`
                              : `Cap. #${row.active.id}`
                          }
                        />
                      ) : row.status === "ready" ? (
                        <span className="muted">Listo</span>
                      ) : row.error_message ? (
                        <span className="error-cell">{row.error_message}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      <button type="button" onClick={() => startEditSeries(row)}>
                        Editar ficha
                      </button>
                      <button type="button" onClick={() => setSeriesModal(row)}>
                        Capítulos
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => reprocessSeries(row.episodes)}
                      >
                        Reprocesar
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={busy}
                        onClick={() => removeSeries(row.episodes)}
                      >
                        Borrar
                      </button>
                    </td>
                  </tr>
                );
              }

              const v = row.video;
              const converting = v.status === "queued" || v.status === "processing";
              return (
                <tr
                  key={`movie-${v.id}`}
                  className="admin-row admin-stagger"
                  style={{ "--i": index }}
                >
                  <td>
                    {v.cover_url ? (
                      <img className="admin-thumb" src={v.cover_url} alt={v.title} />
                    ) : (
                      <div className="admin-thumb fallback">—</div>
                    )}
                  </td>
                  <td data-label="ID">{v.id}</td>
                  <td data-label="Título">{v.title}</td>
                  <td data-label="Tipo">
                    <span className="type-pill">
                      {CONTENT_TYPES.find((t) => t.value === (v.content_type || "movie"))
                        ?.label || "Película"}
                    </span>
                  </td>
                  <td data-label="Nota">
                    {v.rating_avg != null ? (
                      <span className="admin-rating-pill">
                        ★ {Number(v.rating_avg).toFixed(1)}
                        {v.admin_rating != null ? (
                          <small> admin</small>
                        ) : v.rating_count ? (
                          <small> {v.rating_count}</small>
                        ) : null}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td data-label="Estado">
                    <span className={`badge badge-${v.status}`}>{v.status}</span>
                  </td>
                  <td data-label="Conversión" className="admin-convert-cell">
                    {converting ? (
                      <ConvertInlineBar token={token} videoId={v.id} enabled key={`convert-${v.id}`} />
                    ) : v.status === "failed" && v.error_message ? (
                      <span className="error-cell">{v.error_message}</span>
                    ) : v.status === "ready" ? (
                      <span className="muted">Listo</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="actions">
                    <button type="button" onClick={() => startEdit(v)}>
                      Editar
                    </button>
                    <button type="button" onClick={() => reprocess(v.id)}>
                      Reprocesar
                    </button>
                    <button type="button" className="danger" onClick={() => removeVideo(v.id)}>
                      Borrar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!pageItems.length ? (
          <p className="muted admin-empty">No hay vídeos que coincidan con la búsqueda.</p>
        ) : null}
      </div>

      <div className="admin-pager">
        <p className="admin-pager-meta">
          {activeCatalogRows.length
            ? `Mostrando ${rangeFrom}–${rangeTo} de ${activeCatalogRows.length} ${catalogKindLabelPlural}`
            : "Sin vídeos"}
        </p>
        <div className="admin-pager-controls">
          <label className="admin-page-size">
            Por página
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button type="button" disabled={safePage <= 1} onClick={() => setPage(1)}>
            «
          </button>
          <button
            type="button"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          {pageButtons.map((n) => (
            <button
              key={n}
              type="button"
              className={n === safePage ? "active" : ""}
              onClick={() => setPage(n)}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            disabled={safePage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Siguiente
          </button>
          <button
            type="button"
            disabled={safePage >= totalPages}
            onClick={() => setPage(totalPages)}
          >
            »
          </button>
        </div>
      </div>
        </div>
      )}
      </div>
      {seriesModal ? (
        <SeriesEpisodesModal
          token={token}
          seriesTitle={seriesModal.title}
          episodes={seriesModal.episodes}
          onClose={() => setSeriesModal(null)}
          onEdit={(v) => {
            setSeriesModal(null);
            startEdit(v);
          }}
          onEditSeries={() => {
            const group = seriesModal;
            setSeriesModal(null);
            startEditSeries(group);
          }}
          onReprocess={(id) => reprocess(id)}
          onRemove={(id) => removeVideo(id)}
        />
      ) : null}
    </Shell>
  );
}
